import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterOptions, SortOption, LorcanaCard } from '../types';
import { useCollection } from '../contexts/CollectionContext';
import { useCardData } from '../contexts/CardDataContext';
import { usePagination } from './usePagination';
import { useDebounce } from './useDebounce';
import { filterCards, sortCards, groupCards, countActiveFilters, computeContextualFilterOptions } from '../utils/cardFiltering';
import { getDefaultFilters, parseURLState } from '../utils/filterDefaults';
import { PAGINATION } from '../constants';

export const useCardBrowser = (cardData: LorcanaCard[] = []) => {
  const { sets, costRange, strengthRange, willpowerRange, loreRange } = useCardData();
  // ================================
  // 1. EXTERNAL DEPENDENCIES
  // ================================
  const { getCardQuantity, addCardToCollection, removeCardFromCollection } = useCollection();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ================================
  // 2. STATE INITIALIZATION & URL SYNC
  // ================================
  const urlState = parseURLState(searchParams);
  const [searchTerm, setSearchTermState] = useState(urlState.searchTerm);
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // 300ms debounce
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(urlState.viewMode);
  const [sortBy, setSortByState] = useState<SortOption>(urlState.sortBy);
  const [groupBy, setGroupByState] = useState<string>(urlState.groupBy);
  const [filters, setFiltersState] = useState<FilterOptions>(urlState.filters);
  const [showFilters, setShowFilters] = useState(false);

  // ================================
  // 2b. SYNC STATE FROM URL CHANGES (e.g., when navigating from wizard)
  // ================================
  useEffect(() => {
    const newUrlState = parseURLState(searchParams);

    // Only update if URL has meaningful filter params (not just empty)
    const hasUrlFilters = searchParams.toString().length > 0;

    if (hasUrlFilters) {
      // Check if filters actually changed to avoid unnecessary updates
      const currentColors = JSON.stringify(filters.colors);
      const newColors = JSON.stringify(newUrlState.filters.colors);
      const currentTypes = JSON.stringify(filters.types);
      const newTypes = JSON.stringify(newUrlState.filters.types);
      const currentStories = JSON.stringify(filters.stories);
      const newStories = JSON.stringify(newUrlState.filters.stories);
      const currentRarities = JSON.stringify(filters.rarities);
      const newRarities = JSON.stringify(newUrlState.filters.rarities);

      if (currentColors !== newColors || currentTypes !== newTypes ||
          currentStories !== newStories || currentRarities !== newRarities ||
          filters.inkwellOnly !== newUrlState.filters.inkwellOnly ||
          filters.costMin !== newUrlState.filters.costMin ||
          filters.costMax !== newUrlState.filters.costMax) {
        setFiltersState(newUrlState.filters);
      }

      if (searchTerm !== newUrlState.searchTerm) {
        setSearchTermState(newUrlState.searchTerm);
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ================================
  // 3. STALE CARD TRACKING STATE
  // ================================
  const [staleCardIds, setStaleCardIds] = useState<Set<number>>(new Set());
  const [showFilterNotification, setShowFilterNotification] = useState(false);
  const [staleCardCount, setStaleCardCount] = useState(0);
  
  // ================================
  // 4. COLLECTION HELPERS
  // ================================
  // No more getVariantQuantities - we work with individual cards directly

  // Track window size for responsive pagination
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ================================
  // 5. SYNC RANGE FILTERS WITH ACTUAL DATA RANGES
  // ================================
  // When actual card data ranges load, update filters to use real ranges
  // This prevents range filters from appearing as "active" with default values
  useEffect(() => {
    // Only sync when we have valid ranges (max > 0 means data has loaded)
    const hasValidRanges = strengthRange?.max > 0 || willpowerRange?.max > 0 ||
                           loreRange?.max > 0 || costRange?.max > 0;

    if (hasValidRanges) {
      setFiltersState(prev => {
        const defaults = getDefaultFilters();

        // Only update a range if the data has loaded for that range (max > 0)
        // and the filter is still at its default value
        const updates: Partial<typeof prev> = {};

        if (strengthRange?.max > 0) {
          if (prev.strengthMin === defaults.strengthMin) updates.strengthMin = strengthRange.min;
          if (prev.strengthMax === defaults.strengthMax) updates.strengthMax = strengthRange.max;
        }
        if (willpowerRange?.max > 0) {
          if (prev.willpowerMin === defaults.willpowerMin) updates.willpowerMin = willpowerRange.min;
          if (prev.willpowerMax === defaults.willpowerMax) updates.willpowerMax = willpowerRange.max;
        }
        if (loreRange?.max > 0) {
          if (prev.loreMin === defaults.loreMin) updates.loreMin = loreRange.min;
          if (prev.loreMax === defaults.loreMax) updates.loreMax = loreRange.max;
        }
        if (costRange?.max > 0) {
          if (prev.costMin === defaults.costMin) updates.costMin = costRange.min;
          if (prev.costMax === defaults.costMax) updates.costMax = costRange.max;
        }

        if (Object.keys(updates).length > 0) {
          return { ...prev, ...updates };
        }
        return prev;
      });
    }
  }, [strengthRange, willpowerRange, loreRange, costRange]);

  // ================================
  // 6. COMPUTED VALUES & EFFECTS
  // ================================
  // Dynamic pagination based on viewport and view mode
  const cardsPerPage = useMemo(() => {
    if (viewMode === 'list') {
      // List view can show more items based on screen size
      const { width, height } = windowSize;
      
      // Estimate based on screen capacity
      if (width >= 1920 && height >= 1080) {
        return 300; // 2xl screens with 4 columns
      } else if (width >= 1536) {
        return 200; // xl screens with 3 columns
      } else if (width >= 1280) {
        return 150; // lg screens with 2 columns
      }
    }
    return PAGINATION.CARDS_PER_PAGE; // Default 100
  }, [viewMode, windowSize]);

  const { sortedCards, groupedCards, totalCards, activeFiltersCount, contextualOptions } = useMemo(() => {
    const filtered = filterCards(cardData, debouncedSearchTerm, filters, staleCardIds, getCardQuantity);
    const sorted = sortCards(filtered, sortBy);
    const grouped = groupCards(sorted, groupBy, sets);
    const activeCount = countActiveFilters(filters, { costRange, strengthRange, willpowerRange, loreRange });

    // Compute contextual filter options based on current filters
    const contextual = computeContextualFilterOptions(cardData, debouncedSearchTerm, filters, getCardQuantity);

    return {
      sortedCards: sorted,
      groupedCards: grouped,
      totalCards: sorted.length,
      activeFiltersCount: activeCount,
      contextualOptions: contextual
    };
  }, [cardData, debouncedSearchTerm, filters, sortBy, groupBy, staleCardIds, getCardQuantity, sets, costRange, strengthRange, willpowerRange, loreRange]);
  
  const pagination = usePagination({
    totalItems: totalCards,
    itemsPerPage: cardsPerPage,
    resetTriggers: [debouncedSearchTerm, filters, sortBy, groupBy]
  });
  
  const paginatedCards = useMemo(() => {
    return sortedCards.slice(pagination.startIndex, pagination.endIndex);
  }, [sortedCards, pagination.startIndex, pagination.endIndex]);

  // ================================
  // 5. URL UPDATE UTILITIES
  // ================================
  const updateURLParams = useCallback((params: Record<string, string | string[] | undefined>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      
      Object.entries(params).forEach(([key, value]) => {
        newParams.delete(key);
        
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach(v => newParams.append(key, v));
          } else {
            newParams.set(key, value);
          }
        }
      });
      
      return newParams;
    });
  }, [setSearchParams]);

  // ================================
  // 6. STATE SETTERS (URL-synced)
  // ================================
  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
    // URL update will happen via useEffect when debouncedSearchTerm changes
  }, []);
  
  // Update URL when debounced search term changes
  useEffect(() => {
    updateURLParams({ search: debouncedSearchTerm || undefined });
  }, [debouncedSearchTerm, updateURLParams]);

  const setSortBy = useCallback((sort: SortOption) => {
    setSortByState(sort);
    updateURLParams({ 
      sortField: sort.field, 
      sortDirection: sort.direction 
    });
  }, [updateURLParams]);

  const setGroupBy = useCallback((group: string) => {
    setGroupByState(group);
    updateURLParams({ groupBy: group === 'none' ? undefined : group });
  }, [updateURLParams]);

  const setFilters = useCallback((newFilters: FilterOptions) => {
    setFiltersState(newFilters);
    const defaultFilters = getDefaultFilters({ costRange, strengthRange, willpowerRange, loreRange });
    
    const params: Record<string, string | string[] | undefined> = {
      legality: newFilters.legality !== defaultFilters.legality ? newFilters.legality : undefined,
      set: newFilters.sets.length > 0 && JSON.stringify(newFilters.sets) !== JSON.stringify(defaultFilters.sets) ? newFilters.sets : undefined,
      color: newFilters.colors.length > 0 && JSON.stringify(newFilters.colors) !== JSON.stringify(defaultFilters.colors) ? newFilters.colors : undefined,
      rarity: newFilters.rarities.length > 0 && JSON.stringify(newFilters.rarities) !== JSON.stringify(defaultFilters.rarities) ? newFilters.rarities : undefined,
      type: newFilters.types.length > 0 && JSON.stringify(newFilters.types) !== JSON.stringify(defaultFilters.types) ? newFilters.types : undefined,
      story: newFilters.stories.length > 0 && JSON.stringify(newFilters.stories) !== JSON.stringify(defaultFilters.stories) ? newFilters.stories : undefined,
      subtype: newFilters.subtypes.length > 0 && JSON.stringify(newFilters.subtypes) !== JSON.stringify(defaultFilters.subtypes) ? newFilters.subtypes : undefined,
      collection: newFilters.collectionFilter !== defaultFilters.collectionFilter ? newFilters.collectionFilter : undefined,
      inkable: newFilters.inkwellOnly !== defaultFilters.inkwellOnly ? String(newFilters.inkwellOnly) : undefined,
      minCost: newFilters.costMin !== defaultFilters.costMin ? String(newFilters.costMin) : undefined,
      maxCost: newFilters.costMax !== defaultFilters.costMax ? String(newFilters.costMax) : undefined,
    };
    
    updateURLParams(params);
  }, [updateURLParams, costRange, strengthRange, willpowerRange, loreRange]);

  const clearAllFilters = useCallback(() => {
    setFilters(getDefaultFilters({ costRange, strengthRange, willpowerRange, loreRange }));
    setSearchTerm('');
  }, [setFilters, setSearchTerm, costRange, strengthRange, willpowerRange, loreRange]);

  // ================================
  // 7. STALE CARD BUSINESS LOGIC
  // ================================  
  const handleCardQuantityChange = useCallback((
    card: LorcanaCard, 
    normalChange: number,
    foilChange: number
  ) => {
    // Apply the change using the card ID system
    if (normalChange !== 0 || foilChange !== 0) {
      if (normalChange + foilChange > 0) {
        addCardToCollection(card.id, Math.max(0, normalChange), Math.max(0, foilChange));
      } else {
        removeCardFromCollection(card.id, Math.max(0, -normalChange), Math.max(0, -foilChange));
      }
    }
    
    // Check if this card would now be filtered out due to collection filters
    if (filters.collectionFilter !== 'all') {
      console.log('Checking if card would be filtered out...', {
        cardName: card.name,
        filter: filters.collectionFilter,
        normalChange,
        foilChange
      });
      
      // Get current quantities for this specific card ID before the change to predict future state
      const currentQuantities = getCardQuantity(card.id);
      const currentTotalOwned = currentQuantities.total;
      
      // Predict what the state will be after this change
      const predictedTotalOwned = currentTotalOwned + normalChange + foilChange;
      const willBeInCollection = predictedTotalOwned > 0;
      
      // Check if card would be filtered out after the change
      const wouldBeFilteredOut = (
        (filters.collectionFilter === 'owned' && !willBeInCollection) ||  // Filter wants "in collection" but card will not be in collection
        (filters.collectionFilter === 'not-owned' && willBeInCollection)     // Filter wants "not in collection" but card will be in collection
      );
      
      console.log('Stale card prediction:', {
        cardName: card.name,
        currentTotalOwned,
        normalChange,
        foilChange,
        predictedTotalOwned,
        willBeInCollection,
        filterWantsInCollection: filters.collectionFilter,
        wouldBeFilteredOut,
        currentStaleIds: Array.from(staleCardIds)
      });
      
      if (wouldBeFilteredOut && !staleCardIds.has(card.id)) {
        console.log('Adding card to stale list:', card.name);
        setStaleCardIds(prev => {
          const newSet = new Set(prev);
          newSet.add(card.id);
          return newSet;
        });
        setStaleCardCount(prev => prev + 1);
        setShowFilterNotification(true);
      }
    }
  }, [filters.collectionFilter, staleCardIds, addCardToCollection, removeCardFromCollection, getCardQuantity]);

  const refreshStaleCards = useCallback(() => {
    setStaleCardIds(new Set());
    setShowFilterNotification(false);
    setStaleCardCount(0);
  }, []);

  const dismissFilterNotification = useCallback(() => {
    setShowFilterNotification(false);
  }, []);

  // ================================
  // 8. EFFECTS
  // ================================
  // Clear stale cards when filters change (user is intentionally refreshing)
  useEffect(() => {
    setStaleCardIds(new Set());
    setShowFilterNotification(false);
    setStaleCardCount(0);
  }, [filters, debouncedSearchTerm, sortBy, groupBy]);

  // ================================
  // 9. RETURN INTERFACE
  // ================================
  return {
    // State
    searchTerm,
    filters,
    sortBy,
    groupBy,
    viewMode,
    showFilters,
    staleCardIds,
    showFilterNotification,
    staleCardCount,
    
    // Actions
    setSearchTerm,
    setFilters,
    setSortBy,
    setGroupBy,
    setViewMode,
    setShowFilters,
    clearAllFilters,
    handleCardQuantityChange,
    refreshStaleCards,
    dismissFilterNotification,
    
    // Computed
    sortedCards,
    groupedCards,
    totalCards,
    activeFiltersCount,
    paginatedCards,
    pagination,
    contextualOptions
  };
};